import { ChangeDetectorRef, ErrorHandler, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { NotifiableError, OnErrorCallback } from '@bugsnag/core/types/common';
import Event from '@bugsnag/core/types/event';
import Bugsnag from '@bugsnag/js';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { firstValueFrom } from 'rxjs';
import { I18nService } from '@app/i18n';
import { LocalStorageService } from '@shared/services/localStorage.service';
import { UrlEndpoint } from '@app/utils/url';
import { environment } from '@src/environments/environment';
import { HttpErrorResponse } from '@angular/common/http';

export interface ErrorInputs {
  title?: string;
  body?: string;
  hideButtons?: boolean;
  showPlLogo?: boolean;
  hideLoggedInButtonsInHeader?: boolean;
}

@Injectable()
export class PotentialifeErrorHandler implements ErrorHandler {
  constructor(private errorService: ErrorService) {}
  public handleError(error: Error): void {
    if (error instanceof HttpErrorResponse) {
      console.log('PotentialifeErrorHandler 401 Error, do nothing as HttpErrorHandlerInterceptor handles it');
      // HttpErrorHandlerInterceptor already handles errors during HTTP requests
      return;
    }

    console.error('PotentialifeErrorHandler handleError: ', error);

    const chunkFailedMessage: RegExp = /Loading chunk [\d]+ failed/;
    const zendeskTimeoutCorsError: RegExp = /Error: timeout[\r\n\s]+finishLoading/;
    if (chunkFailedMessage.test(error?.message)) {
      window.location.reload();
    } else if (zendeskTimeoutCorsError.test(error?.message)) {
      console.log('Error Loading Zendesk, probably third parties cookies are blocked');
    } else {
      this.errorService.showError();
      this.errorService.notifyBugsnag(error);
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private _error: WritableSignal<ErrorInputs | null> = signal<ErrorInputs | null>(null);
  private _isMaintenanceInProgress: WritableSignal<boolean | null> = signal<boolean | null>(null);

  private _shellChangeDetectorRef: ChangeDetectorRef | null = null;

  constructor(
    private httpService: HttpService,
    private i18nService: I18nService,
    private localStorageService: LocalStorageService,
  ) {}

  public setShellChangeDetectorRef(ref: ChangeDetectorRef): void {
    this._shellChangeDetectorRef = ref;
  }

  public async getErrorIfMaintenanceIsInProgress(): Promise<ErrorInputs | null> {
    if (this._isMaintenanceInProgress() === null) {
      // When isMaintenanceDisabled returns an error, it means we are doing some maintenance. When doing this, we need to
      // have a way to check if the website is working before putting it live again. By adding "overwriteMaintenanceMode"
      // in localStorage, we prevent the check to the endpoint and verify ourselves that the website is working before deactivating maintenance mode
      if (this.localStorageService.getFromLocalStorage('overwriteMaintenanceMode', false)) {
        this._isMaintenanceInProgress.set(false);
      } else {
        const requestOptions: HttpQueryInterface = {
          type: HttpRequestType.GET,
          url: UrlEndpoint.isMaintenanceDisabled,
        };

        try {
          await firstValueFrom(this.httpService.request(requestOptions));
          this._isMaintenanceInProgress.set(false);
        } catch (error) {
          console.log('TONIO ErrorService getErrorIfMaintenanceIsInProgress error=', error);
          this._isMaintenanceInProgress.set(true);
          console.log('\x1b[41m TONIO ErrorService getErrorIfMaintenanceIsInProgress 0=', 0, '\x1b');
          return {
            title: this.i18nService.translate('errors.down_for_maintenance.title'),
            body: this.i18nService.translate('errors.down_for_maintenance.body'),
            hideLoggedInButtonsInHeader: true,
            hideButtons: true,
            showPlLogo: true,
          };
        }
      }
    }

    return this._isMaintenanceInProgress()
      ? {
          title: this.i18nService.translate('errors.down_for_maintenance.title'),
          body: this.i18nService.translate('errors.down_for_maintenance.body'),
          hideLoggedInButtonsInHeader: true,
          hideButtons: true,
          showPlLogo: true,
        }
      : null;
  }

  public notifyBugsnag(
    error: NotifiableError,
    onError?: OnErrorCallback,
    postReportCallback?: (err: any, event: Event) => void,
  ): void {
    if (environment.production) {
      Bugsnag.notify(error, onError, postReportCallback);
    } else {
      console.warn(`Not sending to bugsnag as we are in local mode error=`, error);
    }
  }

  public showError(error?: ErrorInputs): void {
    console.log('ErrorService showError error=', error);
    this._error.set({
      title: error?.title,
      body: error?.body,
      hideButtons: error?.hideButtons,
      showPlLogo: error?.showPlLogo,
      hideLoggedInButtonsInHeader: error?.hideLoggedInButtonsInHeader,
    });

    // Shell component is using _error, but for some reason, I have to force the update to see the error displayed
    // using this trick, when we set an error, the view is updated automatically
    if (this._shellChangeDetectorRef) {
      this._shellChangeDetectorRef.detectChanges();
    }
  }

  public hideError(): void {
    this._error.set(null);
  }

  public get getError(): Signal<ErrorInputs | null> {
    return this._error.asReadonly();
  }
}
